import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { tasksApi, serviceReportsApi, serialNumbersApi } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Calendar as CalendarIcon,
  ChevronLeft,
  ChevronRight,
  Clock,
  Hash,
  Wrench,
  ListTodo,
} from "lucide-react";
import {
  buildEventsByDate,
  buildMonthGrid,
  buildWeek,
  buildDay,
  eventStyle,
  addDays,
} from "@/lib/crmCalendar";

const VIEWS = [
  { key: "month", label: "Monat" },
  { key: "week", label: "Woche" },
  { key: "day", label: "Tag" },
];

const weekDayHeaders = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];

function EventPill({ ev, onClick }) {
  const Icon = ev.type === "visit" ? Wrench : ev.type === "serial" ? Hash : ListTodo;
  return (
    <button
      type="button"
      onClick={() => onClick(ev)}
      className={`w-full text-left text-xs px-1.5 py-0.5 rounded border truncate flex items-center gap-1 transition-transform hover:scale-[1.02] ${eventStyle(ev)}`}
      title={ev.title}
      data-testid={`cal-event-${ev.type}-${ev.id}`}
    >
      <Icon className="w-3 h-3 shrink-0" />
      {ev.startTime && <span className="font-medium">{ev.startTime}</span>}
      <span className="truncate">{ev.title}</span>
    </button>
  );
}

export default function Kalender() {
  const navigate = useNavigate();
  const [view, setView] = useState("month");
  const [viewDate, setViewDate] = useState(new Date());
  const [tasks, setTasks] = useState([]);
  const [reports, setReports] = useState([]);
  const [serials, setSerials] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [tRes, rRes, sRes] = await Promise.allSettled([
        tasksApi.getAll(),
        serviceReportsApi.getAll(),
        serialNumbersApi.getCalendar(),
      ]);
      setTasks(tRes.status === "fulfilled" && Array.isArray(tRes.value.data) ? tRes.value.data : []);
      setReports(rRes.status === "fulfilled" && Array.isArray(rRes.value.data) ? rRes.value.data : []);
      setSerials(sRes.status === "fulfilled" && Array.isArray(sRes.value.data) ? sRes.value.data : []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const eventsByDate = useMemo(
    () => buildEventsByDate({ tasks, reports, serials }),
    [tasks, reports, serials]
  );

  const monthData = useMemo(() => buildMonthGrid(viewDate, eventsByDate), [viewDate, eventsByDate]);
  const weekData = useMemo(() => buildWeek(viewDate, eventsByDate), [viewDate, eventsByDate]);
  const dayData = useMemo(() => buildDay(viewDate, eventsByDate), [viewDate, eventsByDate]);

  const label = view === "month" ? monthData.label : view === "week" ? weekData.label : dayData.label;

  const navigateRange = (dir) => {
    if (view === "month") {
      setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() + dir, 1));
    } else if (view === "week") {
      setViewDate(addDays(viewDate, dir * 7));
    } else {
      setViewDate(addDays(viewDate, dir));
    }
  };

  const handleEventClick = (ev) => {
    if (ev.type === "task") navigate("/tasks");
    else if (ev.type === "visit") navigate(`/service-reports/${ev.id}/edit`);
    else if (ev.type === "serial") navigate("/serial-numbers");
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="calendar-page">
      {/* Kopf */}
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-lg bg-[#1A3A5C] text-white flex items-center justify-center shrink-0">
            <CalendarIcon className="w-6 h-6" />
          </div>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-[#0F172A] dark:text-white">Kalender</h1>
            <p className="text-sm text-muted-foreground">Aufgaben, Servicebesuche und Abläufe auf einen Blick</p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex rounded-lg border border-border bg-muted/40 p-0.5" data-testid="calendar-view-toggle">
            {VIEWS.map((v) => (
              <button
                key={v.key}
                type="button"
                onClick={() => setView(v.key)}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  view === v.key ? "bg-[#1A3A5C] text-white shadow-sm" : "text-muted-foreground hover:text-foreground"
                }`}
                data-testid={`calendar-view-${v.key}`}
              >
                {v.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-1">
            <Button variant="outline" size="icon" onClick={() => navigateRange(-1)} data-testid="calendar-prev-btn">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={() => setViewDate(new Date())} data-testid="calendar-today-btn">
              Heute
            </Button>
            <Button variant="outline" size="icon" onClick={() => navigateRange(1)} data-testid="calendar-next-btn">
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Monatslabel + Legende */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-lg font-semibold capitalize" data-testid="calendar-range-label">{label}</h2>
        <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
          <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-emerald-400" /> Servicebesuch</span>
          <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-red-400" /> Aufgabe (Hoch)</span>
          <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-amber-400" /> Aufgabe (Mittel)</span>
          <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-orange-400" /> Ablauf</span>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24">
          <div className="spinner" />
        </div>
      ) : (
        <Card>
          <CardContent className="p-2 sm:p-4">
            {view === "month" && (
              <div className="grid grid-cols-7 gap-1" data-testid="calendar-month">
                {weekDayHeaders.map((d) => (
                  <div key={d} className="py-2 text-center text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                    {d}
                  </div>
                ))}
                {monthData.cells.map((cell) => (
                  <div
                    key={cell.key}
                    className={`min-h-[96px] sm:min-h-[116px] rounded-md border p-1 flex flex-col gap-1 ${
                      cell.inMonth ? "bg-background" : "bg-muted/30"
                    } ${cell.isToday ? "border-[#E8700A] border-2" : "border-border"}`}
                    data-testid={`calendar-day-${cell.key}`}
                  >
                    <div
                      className={`text-xs font-semibold self-end ${
                        cell.isToday
                          ? "text-white bg-[#E8700A] rounded-full w-5 h-5 flex items-center justify-center"
                          : cell.inMonth
                          ? "text-foreground"
                          : "text-muted-foreground/50"
                      }`}
                    >
                      {cell.day}
                    </div>
                    <div className="space-y-0.5 overflow-hidden">
                      {cell.events.slice(0, 3).map((ev) => (
                        <EventPill key={ev.id} ev={ev} onClick={handleEventClick} />
                      ))}
                      {cell.events.length > 3 && (
                        <div className="text-[11px] text-muted-foreground text-center">+{cell.events.length - 3} mehr</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {view === "week" && (
              <div className="grid grid-cols-1 sm:grid-cols-7 gap-2" data-testid="calendar-week">
                {weekData.days.map((d) => (
                  <div
                    key={d.key}
                    className={`rounded-md border p-2 min-h-[160px] ${d.isToday ? "border-[#E8700A] border-2" : "border-border"}`}
                    data-testid={`calendar-week-day-${d.key}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-semibold text-muted-foreground uppercase">{d.weekday}</span>
                      <span className={`text-sm font-bold ${d.isToday ? "text-[#E8700A]" : ""}`}>{d.dayNum}</span>
                    </div>
                    <div className="space-y-1">
                      {d.events.length === 0 ? (
                        <p className="text-[11px] text-muted-foreground/60">—</p>
                      ) : (
                        d.events.map((ev) => <EventPill key={ev.id} ev={ev} onClick={handleEventClick} />)
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {view === "day" && (
              <div className="max-w-2xl mx-auto space-y-2 py-2" data-testid="calendar-day-view">
                {dayData.events.length === 0 ? (
                  <div className="text-center py-16 text-muted-foreground">
                    <CalendarIcon className="w-10 h-10 mx-auto mb-3 opacity-40" />
                    <p className="text-sm">Keine Einträge an diesem Tag</p>
                  </div>
                ) : (
                  dayData.events.map((ev) => {
                    const Icon = ev.type === "visit" ? Wrench : ev.type === "serial" ? Hash : ListTodo;
                    return (
                      <button
                        key={ev.id}
                        type="button"
                        onClick={() => handleEventClick(ev)}
                        className={`w-full text-left rounded-lg border p-3 flex items-start gap-3 transition-transform hover:scale-[1.01] ${eventStyle(ev)}`}
                        data-testid={`calendar-day-event-${ev.type}-${ev.id}`}
                      >
                        <Icon className="w-5 h-5 mt-0.5 shrink-0" />
                        <div className="min-w-0">
                          <p className="font-semibold truncate">{ev.title}</p>
                          <p className="text-xs opacity-80 truncate">
                            {ev.type === "visit" && (
                              <>
                                {ev.startTime && ev.endTime && (
                                  <span className="inline-flex items-center gap-1 mr-2">
                                    <Clock className="w-3 h-3" />
                                    {ev.startTime}–{ev.endTime}
                                  </span>
                                )}
                                {ev.customer} {ev.technician ? `· ${ev.technician}` : ""}
                              </>
                            )}
                            {ev.type === "task" && <>{ev.customer || "Aufgabe"} {ev.assignee ? `· ${ev.assignee}` : ""}</>}
                            {ev.type === "serial" && <>{ev.product} · {ev.customer}</>}
                          </p>
                        </div>
                      </button>
                    );
                  })
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
